import { useMemo } from "react";

import { getLocale, plural, useT, type TKey } from "@/lib/i18n";
import { countryCatalog } from "@/lib/countries";
import { trafficClassLabel } from "@/lib/traffic-class";
import { useCampaignsByIds, useListCampaignGroups } from "@/lib/services/campaign-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import { useTrafficSourcesByIds } from "@/lib/services/traffic-source-service";
import { useAllAffiliateNetworks } from "@/lib/services/network-service";
import { usePeopleNames } from "@/lib/services/workspace-service";
import { useFlowsByIds } from "@/lib/services/flow-service";
import { useListEntryPoints } from "@/lib/services/entry-point-service";
import type { CatalogKind } from "@/components/canonical/pickers/catalog-picker";
import type { CampaignListItem } from "@gen/tracker/v1/campaign_pb";

/** What `coalesce(…, toUUID('0…0'))` means on the wire: the row had none. */
export const NIL_UUID = "00000000-0000-0000-0000-000000000000";
/** The server's path value of spend no click carries (repository.SpendWithoutClicks). */
export const SPEND_WITHOUT_CLICKS = "∅";
const WEEKDAYS: TKey[] = ["wd.1", "wd.2", "wd.3", "wd.4", "wd.5", "wd.6", "wd.7"];
/** `origin`: whether the campaign is the tracker's own or another block's import. */
const ORIGIN: Record<string, TKey> = { native: "origin.native", imported: "origin.imported" };
/** `entry_match`: how an entry point picked the campaign (rule:<i> reads «Правило N»). */
const ENTRY_MATCH: Record<string, TKey> = {
  "selector:query_key": "entryMatch.queryKey",
  "selector:query_value": "entryMatch.queryValue",
  sticky: "entryMatch.sticky",
  fallback: "entryMatch.fallback",
};
const STATUS: Record<string, TKey> = { APPROVED: "status.approved", HOLD: "status.hold", REJECTED: "status.rejected", TRASH: "status.trash", REFUNDED: "status.refunded" };

/** The dimensions whose values arrive as ids. */
export const ID_DIMENSIONS = ["campaign", "offer", "landing", "source", "network", "flow", "group", "owner", "entry_point"] as const;

/**
 * The catalog a dimension's value is picked from when there are too many to
 * list (campaigns, offers, landers, sources, flows): a filter on one of them
 * searches the catalog instead of offering a page of it. Pure.
 */
export function dimensionCatalog(key: string): CatalogKind | undefined {
  switch (key) {
    case "campaign":
      return "campaign";
    case "offer":
      return "offer";
    case "landing":
      return "lander";
    case "source":
      return "source";
    case "flow":
      return "flow";
    default:
      return undefined;
  }
}

/** The ids each id dimension needs named, from (dimension, value) pairs. Pure. */
export function idsByDimension(values: Iterable<readonly [string, string]>): Map<string, string[]> {
  const out = new Map<string, Set<string>>();
  for (const [key, value] of values) {
    if (!value || value === NIL_UUID || value === SPEND_WITHOUT_CLICKS) continue;
    if (!(ID_DIMENSIONS as readonly string[]).includes(key)) continue;
    let set = out.get(key);
    if (!set) out.set(key, (set = new Set()));
    set.add(value);
  }
  return new Map([...out].map(([k, v]) => [k, [...v]] as const));
}

/** Every (dimension, value) of report rows: `dims[i]` with `path[i]`. Pure. */
export function rowValues(dims: readonly string[], rows: Iterable<{ dimensionPath: readonly string[] }>): [string, string][] {
  const out: [string, string][] = [];
  for (const r of rows) r.dimensionPath.forEach((v, i) => dims[i] && out.push([dims[i]!, v]));
  return out;
}

/**
 * A dimension value as a person reads it. The server groups by id (a campaign,
 * an offer…) and ships the id; the names are asked for BY THOSE IDS — never
 * looked up in a page of the catalog, which is how a lander past the 200th
 * came to show as «2b042e3c…». A deleted object keeps a short id; an empty
 * value is «—».
 *
 * `values` is every (dimension, value) on screen: the rows and the filters.
 * `held` are campaigns the screen already holds whole (the report's own
 * list of every campaign it reads): their names are taken from there, and
 * only the ids it does not hold are asked for.
 */
export function useDimensionNames(values: Iterable<readonly [string, string]>, held?: readonly CampaignListItem[]) {
  const t = useT();
  const ids = idsByDimension(values);
  const want = (k: string) => ids.get(k) ?? [];
  const heldNames = useMemo(() => new Map((held ?? []).flatMap((i) => (i.campaign ? [[i.campaign.id, i.campaign.name] as const] : []))), [held]);
  const campaigns = useCampaignsByIds(want("campaign").filter((id) => !heldNames.has(id)));
  const offers = useOffersByIds(want("offer"));
  const landers = useLandersByIds(want("landing"));
  const sources = useTrafficSourcesByIds(want("source"));
  const flows = useFlowsByIds(want("flow"));
  // Partners are few and a filter lists them all, so they are read whole.
  const networks = useAllAffiliateNetworks();
  const groups = useListCampaignGroups();
  // «Баер» is a Keycloak sub, named by the realm.
  const people = usePeopleNames(want("owner"));
  // Entry points are few per workspace: read whole, only when one is on screen.
  const entries = useListEntryPoints("", ids.has("entry_point"));

  const maps = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    m.set("campaign", new Map([...heldNames, ...[...(campaigns.data?.values() ?? [])].flatMap((i) => (i.campaign ? [[i.campaign.id, i.campaign.name] as const] : []))]));
    m.set("offer", new Map([...(offers.data?.values() ?? [])].map((o) => [o.id, o.name] as const)));
    m.set("landing", new Map([...(landers.data?.values() ?? [])].map((l) => [l.id, l.name] as const)));
    m.set("source", new Map([...(sources.data?.values() ?? [])].map((s) => [s.id, s.name] as const)));
    m.set("flow", new Map([...(flows.data?.values() ?? [])].map((f) => [f.id, f.name] as const)));
    m.set("network", new Map((networks.data?.items ?? []).map((n) => [n.id, n.name] as const)));
    m.set("group", new Map((groups.data ?? []).map((g) => [g.id, g.name] as const)));
    m.set("owner", new Map(Object.entries(people.data ?? {})));
    m.set("entry_point", new Map((entries.data ?? []).map((e) => [e.id, `${e.name} · ${e.trackingDomain}${e.path === "/" ? "" : e.path}`] as const)));
    return m;
  }, [heldNames, campaigns.data, offers.data, landers.data, sources.data, flows.data, networks.data, groups.data, people.data, entries.data]);

  // Which id dimensions are still being named: a row of one waits for its
  // name under a skeleton instead of flashing «2296c070…» first (У2i).
  const naming: Record<string, boolean> = {
    campaign: campaigns.isFetching || campaigns.isPending,
    offer: offers.isFetching || offers.isPending,
    landing: landers.isFetching || landers.isPending,
    source: sources.isFetching || sources.isPending,
    flow: flows.isFetching || flows.isPending,
    network: networks.isPending,
    group: groups.isPending,
    owner: people.isFetching || people.isPending,
    entry_point: entries.isFetching || entries.isPending,
  };
  /** The value is an id whose name has not arrived yet (it may still: the query is in flight). */
  const pending = (key: string, value: string): boolean => {
    if (!value || value === NIL_UUID || value === SPEND_WITHOUT_CLICKS) return false;
    const byId = maps.get(key);
    return !!byId && !byId.has(value) && !!naming[key];
  };

  const name = (key: string, value: string): string => {
    if (!value || value === NIL_UUID) return DIM_NONE[key] ? t(DIM_NONE[key]!) : "—";
    if (value === SPEND_WITHOUT_CLICKS) return t("card.report.spendWithoutClicks");
    const byId = maps.get(key);
    if (byId) return byId.get(value) ?? `${value.slice(0, 8)}…`;
    if (key === "weekday") return WEEKDAYS[Number(value) - 1] ? t(WEEKDAYS[Number(value) - 1]!) : value;
    if (key === "hour") return `${value.padStart(2, "0")}:00`;
    if (key === "country") {
      // «Румыния · RO», as the boards write a country.
      const c = countryCatalog(getLocale()).find((x) => x.code === value.toUpperCase());
      return c && c.name !== c.code ? `${c.name} · ${c.code}` : value;
    }
    if (key === "traffic_class" || key === "bot_reason") return trafficClassLabel(t, value);
    if (key === "status") return STATUS[value] ? t(STATUS[value]!) : value;
    if (key === "origin") return ORIGIN[value] ? t(ORIGIN[value]!) : value;
    if (key === "entry_match") return entryMatchLabel(t, value);
    return value;
  };
  /**
   * The values a filter picks from, where they are few enough to list
   * (networks, groups, statuses…). A catalog dimension has none — its filter
   * searches the catalog (dimensionCatalog) — and a free one is typed.
   */
  const choices = (key: string): { value: string; label: string }[] | undefined => {
    if (dimensionCatalog(key)) return undefined;
    const byId = maps.get(key);
    if (byId && byId.size) return [...byId].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    if (key === "traffic_class") return ["human", "bot"].map((v) => ({ value: v, label: trafficClassLabel(t, v) }));
    if (key === "status") return Object.keys(STATUS).map((v) => ({ value: v, label: t(STATUS[v]!) }));
    if (key === "origin") return Object.keys(ORIGIN).map((v) => ({ value: v, label: t(ORIGIN[v]!) }));
    if (key === "entry_match") return Object.keys(ENTRY_MATCH).map((v) => ({ value: v, label: t(ENTRY_MATCH[v]!) }));
    if (key === "weekday") return WEEKDAYS.map((k, i) => ({ value: String(i + 1), label: t(k) }));
    return undefined;
  };
  return { name, pending, choices, landers: landers.data, offers: offers.data };
}

/** A row with no value of an id dimension, in words: «Без партнёрки», not «—». */
const DIM_NONE: Record<string, TKey> = {
  network: "dimNone.network",
  offer: "dimNone.offer",
  landing: "dimNone.landing",
  campaign: "dimNone.campaign",
  source: "dimNone.source",
  group: "dimNone.group",
  creative: "dimNone.creative",
  flow: "dimNone.flow",
  owner: "dimNone.owner",
  entry_point: "dimNone.entry_point",
  entry_match: "dimNone.entry_point",
};

/** «По ключу в ссылке», «Правило 2» — how an entry point picked the campaign. Pure. */
export function entryMatchLabel(t: (k: TKey, v?: Record<string, string | number>) => string, value: string): string {
  if (ENTRY_MATCH[value]) return t(ENTRY_MATCH[value]!);
  const rule = /^rule:(\d+)$/.exec(value);
  return rule ? t("entryMatch.rule", { n: Number(rule[1]) + 1 }) : value;
}

const DIM_WORDS: Record<string, TKey> = {
  campaign: "dimWord.campaign",
  creative: "dimWord.creative",
  landing: "dimWord.landing",
  offer: "dimWord.offer",
  network: "dimWord.network",
  source: "dimWord.source",
  country: "dimWord.country",
  ad_campaign: "dimWord.ad_campaign",
  ad_set: "dimWord.ad_set",
  placement: "dimWord.placement",
  group: "dimWord.group",
  flow: "dimWord.flow",
  node: "dimWord.node",
  step: "dimWord.step",
  branch: "dimWord.branch",
  region: "dimWord.region",
  city: "dimWord.city",
  language: "dimWord.language",
  device: "dimWord.device",
  os: "dimWord.os",
  browser: "dimWord.browser",
  device_model: "dimWord.device_model",
  isp: "dimWord.isp",
  asn: "dimWord.asn",
  connection: "dimWord.connection",
  ip_subnet: "dimWord.ip_subnet",
  referrer: "dimWord.referrer",
  hour: "dimWord.hour",
  weekday: "dimWord.weekday",
  day: "dimWord.day",
  week: "dimWord.week",
  month: "dimWord.month",
  goal: "dimWord.goal",
  status: "dimWord.status",
  traffic_class: "dimWord.traffic_class",
  bot_reason: "dimWord.bot_reason",
  owner: "dimWord.owner",
  entry_point: "dimWord.entry_point",
};

/**
 * «3 кампании», «1 объявление» — how many values of a dimension, in words.
 * A dimension without its own word (sub1…sub20) reads «3 · sub1». Pure.
 */
export function dimCount(t: (k: TKey) => string, key: string, n: number, label: string, nText: string = String(n)): string {
  const word = DIM_WORDS[key];
  return word ? `${nText} ${plural(t(word), n)}` : `${nText} · ${label.toLowerCase()}`;
}
